import React, { FC, useEffect, useState } from 'react';
import { Trash2 } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import ToggleSwitch from '../../../components/inputs/toggle/ToggleSwitch';
import Tag from '../../../components/tags/Tag';
import Typography from '../../../components/typography/Typography';
import { clearCyodSelection } from '../../../state/shop/cyodSlice';
import { clearCart, setBuyerProfile } from '../../../state/shop/shopSlice';
import { RootState } from '../../../state/store';
import { UserRoles, hasRole } from '../../../utils/auth/AuthorizationUtility';
import CalculateTotalPrice from '../../../utils/cart/CalculateTotalPrice';
import CurrencyFormatter from '../../../utils/text/CurrencyFormatter';

interface priceOverviewContainer {
  paymentActive?: boolean;
}

const PriceOverviewContainer: FC<priceOverviewContainer> = ({
  paymentActive = false,
}) => {
  const roles = useSelector((state: RootState) => state.auth.roles);
  const cart = useSelector((state: RootState) => state.cart);
  const cyod = useSelector((state: RootState) => state.cyod);
  const branch = useSelector((state: RootState) => state.branch.selectedBranch);
  const [admin, setAdmin] = useState(false);
  const [isBuyerProfile, setIsBuyerProfile] = useState(false);

  const branchState = useSelector((state: RootState) => state.branch);
  const [cartTotals, setCartTotals] = useState({});
  const [isCyod, setIsCyod] = useState(false);
  const dispatch = useDispatch();
  const handleClearCart = () => {
    dispatch(clearCart());
    dispatch(clearCyodSelection());
  };

  useEffect(() => {
    const vatApplicable =
      !branchState.invoiceAddress?.ves_land ||
      branchState.invoiceAddress?.ves_land === 'België' ||
      cyod.usedCoupon > 0;

    const deliveryCosts =
      !branchState.selectedBranch?.ves_land ||
      branchState.selectedBranch?.ves_land === 'België';

    const newVatPercentage = vatApplicable ? 1.21 : 1.0;
    const newLeveringskost = !deliveryCosts ? 15 : 0;
    calculatePrices(newVatPercentage, newLeveringskost);
  }, [branchState, cyod, cart]);

  const calculatePrices = (vatPercentage: number, leveringskost: number) => {
    const cartTotal = CalculateTotalPrice(cart, cyod) - cyod.usedCoupon;
    const btw = cartTotal * vatPercentage - cartTotal;
    const cartTotalWithVat = cartTotal + btw + leveringskost;

    setCartTotals({
      excl: cartTotal,
      btw: btw,
      levering: leveringskost,
      incl: cartTotalWithVat,
    });
  };

  const changeBuyerProfile = () => {
    const profile = !isBuyerProfile;
    console.log('profile', profile);
    dispatch(setBuyerProfile(profile));
    setIsBuyerProfile(profile);
  };

  useEffect(() => {
    setIsCyod(cyod.selectedProducts[0] ? true : false);
    setIsBuyerProfile(cart.buyersProfile ? cart.buyersProfile : false);
    const fetchAdmin = async () => {
      const admin = hasRole(roles, UserRoles.ADMIN);
      setAdmin(admin);
    };
    fetchAdmin();
  }, [roles]);

  return (
    <>
      {cart.totalItems > 0 || Object.keys(cyod.selectedProducts).length > 0 ? (
        // Price overview
        <div className="p-5 relative flex items-center bg-slate-50 dark:bg-slate-800 rounded-t-lg md:rounded-s-lg 2xl:rounded-t-lg">
          <div className="w-full">
            {!paymentActive ? (
              <button
                className="absolute top-5 right-2"
                onClick={handleClearCart}
              >
                <Tag style={'danger'} label={<Trash2 size={18} />}></Tag>
              </button>
            ) : (
              ''
            )}
            <Typography tag={'h1'} type={'subtitle'}>
              Samenvatting
            </Typography>
            <div className="my-2">
              <div className="flex flex-row justify-between">
                <div>
                  <Typography tag={'span'} type={'default'}>
                    Prijs
                  </Typography>
                  &nbsp;
                  <Typography tag="span" type="smallest">
                    excl. BTW
                  </Typography>
                </div>
                <Typography tag={'p'} type={'default'}>
                  <CurrencyFormatter price={CalculateTotalPrice(cart, cyod)} />
                </Typography>
              </div>
              {cyod.usedCoupon > 0 ? (
                <div>
                  <div className="flex flex-row justify-between mb-2">
                    <div>
                      <Typography tag={'span'} type={'default'}>
                        Coupon
                      </Typography>
                      &nbsp;
                      <Typography tag="span" type="smallest">
                        excl. BTW
                      </Typography>
                    </div>
                    <Typography tag={'p'} type={'default'} color="success">
                      -<CurrencyFormatter price={cyod.usedCoupon} />
                    </Typography>
                  </div>
                  <hr />
                  <div className="flex flex-row justify-between mt-2">
                    <div>
                      <Typography tag={'span'} type={'default'}>
                        Totaal
                      </Typography>
                      &nbsp;
                      <Typography tag="span" type="smallest">
                        excl. BTW
                      </Typography>
                    </div>
                    <Typography tag={'p'} type={'default'}>
                      <CurrencyFormatter
                        price={
                          CalculateTotalPrice(cart, cyod) - cyod.usedCoupon
                        }
                      />
                    </Typography>
                  </div>
                </div>
              ) : (
                ''
              )}

              <div className="flex flex-row justify-between">
                <div>
                  <Typography tag={'span'} type={'default'}>
                    Leveringskosten
                  </Typography>
                  &nbsp;
                  <Typography tag="span" type="smallest">
                    excl. BTW
                  </Typography>
                </div>
                <Typography tag={'p'} type={'default'}>
                  <CurrencyFormatter price={cartTotals.levering} />
                </Typography>
              </div>
              <div className="flex flex-row justify-between">
                <Typography tag={'p'} type={'default'}>
                  BTW
                </Typography>
                <Typography tag={'p'} type={'default'}>
                  <CurrencyFormatter price={cartTotals.btw} />
                </Typography>
              </div>
            </div>
            <div className="pt-2 flex flex-row justify-between font-bold border-t dark:border-gray-700">
              <div>
                <Typography tag={'span'} type={'semibold'}>
                  Totaal
                </Typography>
                &nbsp;
                <Typography tag="span" type="smallest">
                  incl. BTW
                </Typography>
              </div>
              <Typography tag={'p'} type={'semibold'}>
                <CurrencyFormatter price={cartTotals.incl} />
              </Typography>
            </div>
            {admin && !isCyod ? (
              <div className="pt-2 flex flex-row justify-between font-bold border-t dark:border-gray-700 mt-5">
                <Typography tag={'span'} type={'default'}>
                  Aankoopprofiel
                </Typography>
                <ToggleSwitch
                  name="buyprofile"
                  onChange={changeBuyerProfile}
                  checked={isBuyerProfile}
                />
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default PriceOverviewContainer;
